import { useCallback, useEffect, useReducer, useState } from 'react'
import { Field, FilteractionObject } from '@yes.technology/react-toolkit'
import { Form } from 'component'
import { FieldValues, FilterAreaLayout, GroupedFieldValues } from '../../types'
import Section from '../Section/Section'
import { useSiteState } from 'siteState/shared'
import { toYesFilter } from '../../shared/toYesFilter/toYesFilter'
import useLocationState from '../../hooks/useLocationState'
import { FieldSettings } from 'shared/types'
import { resolveQuery } from 'filteraction/FilteractionResolver'

type Action = {
  type?: 'merge' | 'set'
  payload: GroupedFieldValues
}

type FilterFormProps = {
  elementUuid: string
  filterAreaLayout?: FilterAreaLayout
  initialGroupedFieldValues: GroupedFieldValues
  sections: Field[][]
  title?: string
  onSubmit?: (query: ReturnType<typeof toYesFilter>) => void
  uuidCurrentSitemodelObjectclass?: string
  defaultExecutedFilteraction?: string
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  style?: React.CSSProperties
  fieldSettings?: FieldSettings
  filteractionOptions?: FilteractionObject[]
}

const FilterForm = ({
  elementUuid,
  filterAreaLayout,
  initialGroupedFieldValues,
  sections,
  title,
  onSubmit,
  uuidCurrentSitemodelObjectclass,
  defaultExecutedFilteraction,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  style,
  fieldSettings,
  filteractionOptions
}: FilterFormProps) => {
  const {
    shouldPersistData,
    setShouldPersistData,
    shouldResetData,
    setShouldResetData,
    persistentSelection,
    targetComponent
  } = useLocationState()

  const fieldValuesReducer = (
    state: GroupedFieldValues,
    action: Action | null
  ) => {
    if (action === null) {
      return initialGroupedFieldValues
    }

    if (action.type === 'set') {
      return action.payload
    }

    return {
      ...state,
      ...Object.fromEntries(
        Object.entries(action.payload).map(([actionGroupKey, actionGroup]) => [
          actionGroupKey,
          { ...state[actionGroupKey], ...actionGroup }
        ])
      )
    }
  }

  const [groupedFieldValues, handleChange] = useReducer(
    fieldValuesReducer,
    initialGroupedFieldValues
  )

  useEffect(() => {
    if (targetComponent !== 'FilterFields') return

    if (shouldPersistData && persistentSelection) {
      handleChange({
        type: 'set',
        payload: persistentSelection as GroupedFieldValues
      })

      setShouldPersistData(false)
    }
  }, [
    shouldPersistData,
    persistentSelection,
    setShouldPersistData,
    targetComponent
  ])

  useEffect(() => {
    if (shouldResetData) {
      handleChange(null)

      setShouldResetData(false)
    }
  }, [shouldResetData, setShouldResetData])

  const [composition, handleComposition] = useState('1')

  const [_, setSiteFieldValues] =
    useSiteState<GroupedFieldValues>('field-values')

  const [, setSiteFieldComposition] = useSiteState<string>('field-composition')

  const handleClear = useCallback(() => {
    handleComposition('1')
    handleChange(null)
  }, [])

  const submitFieldValues = useCallback(
    async (fieldValues: GroupedFieldValues, composition: string) => {
      const groupedValuesNoEmpty = removeEmptyValuesGrouped(fieldValues)

      const query = toYesFilter(groupedValuesNoEmpty, composition)

      if (!query) {
        return
      }

      const resolvedQuery = await resolveQuery(query)

      resolvedQuery && onSubmit?.(resolvedQuery)
      resolvedQuery && setSiteFieldValues(groupedValuesNoEmpty)
      setSiteFieldComposition(composition)
    },
    [onSubmit, setSiteFieldValues, setSiteFieldComposition]
  )

  const onSubmitForm = (event: React.FormEvent) => {
    event.preventDefault()
    submitFieldValues(groupedFieldValues, composition)
  }

  const onFieldChange = useCallback((state: GroupedFieldValues) => {
    handleChange({
      type: 'merge',
      payload: state
    })
  }, [])

  return (
    <Form onSubmit={onSubmitForm} style={style}>
      <Section
        initialFieldValues={initialGroupedFieldValues[1]}
        handleChange={onFieldChange}
        executeFilter={submitFieldValues}
        {...{
          composition,
          sections,
          groupedFieldValues,
          handleClear,
          handleComposition,
          title,
          uuidCurrentSitemodelObjectclass,
          navigationSliderBackgroundColor,
          navigationSliderColor,
          filterAreaLayout,
          defaultExecutedFilteraction,
          elementUuid,
          fieldSettings,
          filteractionOptions
        }}
      />
    </Form>
  )
}

export function removeEmptyValues<T extends FieldValues>(obj: T) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, item]) =>
      Array.isArray(item.value) ? item.value.length > 0 : item.value
    )
  )
}

function removeEmptyValuesGrouped<T extends GroupedFieldValues>(
  groups: T
): GroupedFieldValues {
  return Object.fromEntries(
    Object.entries(groups).map(([group, values]) => [
      group,
      removeEmptyValues(values)
    ])
  )
}

export default FilterForm
